// Enum types

export type PermissionGroup = "USER" | "EMPLOYEE" | "ADMIN";

export const PERMISSION_GROUP_LABELS: Record<PermissionGroup, string> = {
  'ADMIN':    'Administrator',
  'EMPLOYEE': 'Mitarbeiter',
  'USER':     'Benutzer'
};

export const ALL_PERMISSION_GROUPS: PermissionGroup[] = ['ADMIN', 'EMPLOYEE', 'USER'];

export type MessageRole = "system"
                        | "user" 
                        | "assistant"
                        | "assistant_intermediate"
                        | "tool_call"
                        | "tool_response"
                        | "system_query"
                        | "system_query_response"
                        | "system_scratchpad_query"
                        | "system_scratchpad_response";

export type ChatSessionClosureType = "MANUAL" | "AUTOMATIC";

export type ChatSessionType = "GENERIC" | "VIDEO_SESSION" | "VIDEO_SEARCH";

export type JobType = "CLEANUP_JOBLOGS"
                    | "CLOSE_INACTIVE_CHATS"
                    | "DELETE_EMPTY_CHATS"
                    | "UPDATE_SYMBOLS"
                    | "UPDATE_SYMBOLS_EODHD"
                    | "UPDATE_EXCHANGE_RATE"
                    | "EXPIRE_CORE_RECOMMENDATIONS"
                    | "EXPIRE_EARNINGS_RECOMMENDATIONS"
                    | "CHECK_BUNNY_STREAM_PROCESSING"
                    | "CLEANUP_STUCK_VIDEOS"
                    | "UPDATE_OPTION_SCREENER";

export type JobStatus = "RUNNING" | "SUCCESS" | "FAILED";

export type JobLogLevel = "DEBUG"
                        | "INFO"
                        | "WARN"
                        | "ERROR"
                        | "FATAL";

export type MimeType = "image/jpeg"
                     | "image/png"
                     | "application/pdf"
                     | "text/csv"
                     | "video/mp4"
                     | "video/quicktime"
                     | "video/x-matroska"
                     | "video/webm";

export type DataBlobType = "CHAT_USER_ATTACHMENT"
                         | "COURSE_THUMBNAIL"
                         | "MODULE_THUMBNAIL"
                         | "VIDEO_THUMBNAIL";


export type OptionType = "PUT" | "CALL";

export type VideoProcessingState = "WAITING" | "QUEUED" | "ACTIVE" | "DONE" | "ERROR";

// DayTrading enums
export type DayTradingSessionCloseType = "WAITING" | "SUCCESS";
export type DayTradeType = "LONG" | "SHORT";
export type DayTradeState = "OPEN" | "GOAL_REACHED" | "STOPPED" | "STOPLOSS";

// StreamMode — which streaming source backs the day-trading session
export type StreamMode = "NONE" | "YOUTUBE" | "DIRECT" | "GOOGLE_MEET" | "DISCORD";

export const STREAM_MODE_LABELS: Record<StreamMode, string> = {
  'NONE':        'Kein Livestream',
  'YOUTUBE':     'YouTube-Stream',
  'DIRECT':      'Direkter Stream',
  'GOOGLE_MEET': 'Google Meet',
  'DISCORD':     'Discord',
};

export const ALL_STREAM_MODES: StreamMode[] = ['NONE', 'YOUTUBE', 'DIRECT', 'GOOGLE_MEET', 'DISCORD'];

// DayTrading stock count mode
export type DayTradeStockCountMode = "NORMAL" | "FIRST";

// Sort direction enum
export type SortDirection = "ASC" | "DESC";

// Sort field enums
export type ErrorLogSortField = "created" | "updated" | "severity" | "type";

export type JobExecutionSortField = "startTime" | "created" | "updated";

export type JobLogSortField = "created" | "updated" | "_id";

export type ErrorSeverity = "Trace"|"Debug"|"Info"|"Warn"|"Err"|"Fatal";

export type ErrorType = "Wrap"|"System"|"User"|"Foreign";

// exerr.Method enum
export type ExerrMethod = "OUTPUT" | "PRINT" | "BUILD" | "FATAL";

// FeatureType enum - represents the different feature types that can be enabled for users
export type FeatureType =
  | "CORE_RECOMMENDATIONS"
  | "EARNING_RECOMMENDATIONS"
  | "SWINGTRADE_RECOMMENDATIONS"
  | "LONGTERM_RECOMMENDATIONS"
  | "CHATBOT"
  | "VIDEO_CHATBOT"
  | "JOURNAL"
  | "BOOST"
  | "DAY_TRADING"
  | "OPTION_SCREENER"
  | "VIDEOSEARCH"
  | "COMMUNITY_MEMBER";

// All available feature types for dropdowns
export const ALL_FEATURE_TYPES: FeatureType[] = [
  'CORE_RECOMMENDATIONS',
  'EARNING_RECOMMENDATIONS',
  'SWINGTRADE_RECOMMENDATIONS',
  'LONGTERM_RECOMMENDATIONS',
  'CHATBOT',
  'VIDEO_CHATBOT',
  'JOURNAL',
  'BOOST',
  'DAY_TRADING',
  'OPTION_SCREENER',
  'VIDEOSEARCH',
  'COMMUNITY_MEMBER',
];

// Human-readable labels for feature types (German)
export const FEATURE_TYPE_LABELS: Record<FeatureType, string> = {
  'CORE_RECOMMENDATIONS': 'Kernstrategie',
  'EARNING_RECOMMENDATIONS': 'Earnings Trades',
  'SWINGTRADE_RECOMMENDATIONS': 'Swing Trades',
  'LONGTERM_RECOMMENDATIONS': 'Langzeit Trades',
  'CHATBOT': 'Chatbot',
  'VIDEO_CHATBOT': 'Video Chatbot',
  'JOURNAL': 'Journal',
  'BOOST': 'Boost',
  'DAY_TRADING': 'Day Trading',
  'OPTION_SCREENER': 'Options-Screener',
  'VIDEOSEARCH': 'Video-Finder',
  'COMMUNITY_MEMBER': 'Community Mitglied',
};

// EventRecurrenceType - how an event series repeats
export type EventRecurrenceType = "WEEKLY"
                                | "MONTHLY_NTH_WEEKDAY"
                                | "MONTHLY_DAY_OF_MONTH"
                                | "YEARLY";

export const ALL_EVENT_RECURRENCE_TYPES: EventRecurrenceType[] = ['WEEKLY', 'MONTHLY_NTH_WEEKDAY', 'MONTHLY_DAY_OF_MONTH', 'YEARLY'];

export const EVENT_RECURRENCE_TYPE_LABELS: Record<EventRecurrenceType, string> = {
  'WEEKLY':               'Wöchentlich',
  'MONTHLY_NTH_WEEKDAY':  'Monatlich (n-ter Wochentag)',
  'MONTHLY_DAY_OF_MONTH': 'Monatlich (Tag im Monat)',
  'YEARLY':               'Jährlich',
};

// EventWeekday - weekday of a recurrence rule
export type EventWeekday = "MONDAY"
                         | "TUESDAY"
                         | "WEDNESDAY"
                         | "THURSDAY"
                         | "FRIDAY"
                         | "SATURDAY"
                         | "SUNDAY";

export const ALL_EVENT_WEEKDAYS: EventWeekday[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

export const EVENT_WEEKDAY_LABELS: Record<EventWeekday, string> = {
  'MONDAY':    'Montag',
  'TUESDAY':   'Dienstag',
  'WEDNESDAY': 'Mittwoch',
  'THURSDAY':  'Donnerstag',
  'FRIDAY':    'Freitag',
  'SATURDAY':  'Samstag',
  'SUNDAY':    'Sonntag',
};

// EventWeekOrdinal - which occurrence of a weekday within a month
export type EventWeekOrdinal = "FIRST" | "SECOND" | "THIRD" | "FOURTH" | "LAST";

export const ALL_EVENT_WEEK_ORDINALS: EventWeekOrdinal[] = ['FIRST', 'SECOND', 'THIRD', 'FOURTH', 'LAST'];

export const EVENT_WEEK_ORDINAL_LABELS: Record<EventWeekOrdinal, string> = {
  'FIRST':  'Erster',
  'SECOND': 'Zweiter',
  'THIRD':  'Dritter',
  'FOURTH': 'Vierter',
  'LAST':   'Letzter',
};

// EventSeriesScope - which occurrences of a series an update/delete applies to
export type EventSeriesScope = "SINGLE" | "FOLLOWING" | "ALL";

export const ALL_EVENT_SERIES_SCOPES: EventSeriesScope[] = ['SINGLE', 'FOLLOWING', 'ALL'];

export const EVENT_SERIES_SCOPE_LABELS: Record<EventSeriesScope, string> = {
  'SINGLE':    'Nur dieser Termin',
  'FOLLOWING': 'Dieser und alle folgenden',
  'ALL':       'Alle Termine der Serie',
};

// EventImportRowStatus - result of a single csv row during import
export type EventImportRowStatus = "VALID" | "CREATED" | "SKIPPED_EXISTING" | "INVALID";

export const EVENT_IMPORT_ROW_STATUS_LABELS: Record<EventImportRowStatus, string> = {
  'VALID':            'Wird angelegt',
  'CREATED':          'Angelegt',
  'SKIPPED_EXISTING': 'Übersprungen (existiert)',
  'INVALID':          'Fehlerhaft',
};

// EventSeriesSortField - sortable fields of the event-series list endpoint
export type EventSeriesSortField = "created" | "updated" | "start" | "subject";
